import {
  BoxGeometry,
  Euler,
  LineBasicMaterial,
  LineSegments,
  Scene,
  TextureLoader,
  DoubleSide,
  EdgesGeometry,
  BufferGeometry,
  BufferAttribute,
  Group,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  Vector3,
} from 'three';

export class CameraBox {
  public hoverableObjects: Group = new Group();
  private readonly BOX_DIMENSION: number = 4;
  private readonly SIDE_SIZE: number = 0.5;
  private otherObjects: Group = new Group();

  constructor(scene: Scene) {
    const centerFaceGeometry = this.createCenterPlaneGemetry();
    this.drawCenterPlanes(this.hoverableObjects, this.otherObjects, centerFaceGeometry);

    const edgeGeometry = this.createEdgeGeometry();
    this.drawVerticalEdges(this.hoverableObjects, edgeGeometry);
    this.drawBottomEdges(this.hoverableObjects, edgeGeometry);
    this.drawTopEdges(this.hoverableObjects, edgeGeometry);

    const cornerGeometry = this.createCornerGeometry();
    this.drawBottomCorners(this.hoverableObjects, cornerGeometry);
    this.drawTopCorners(this.hoverableObjects, cornerGeometry);
    this.drawBoxStroke(this.otherObjects);
    scene.add(this.hoverableObjects);
    scene.add(this.hoverableObjects);
    scene.add(this.otherObjects);
  }

  private drawBoxStroke(group: Group) {
    const geometry = new BoxGeometry(
      this.BOX_DIMENSION,
      this.BOX_DIMENSION,
      this.BOX_DIMENSION,
    );
    const edges = new EdgesGeometry(geometry);
    const edgesMesh = new LineSegments(edges, new LineBasicMaterial({ color: 0xcdcdcd }));
    group.add(edgesMesh);
  }

  private drawCenterPlanes(hoverableGroup: Group, otherGroup: Group, faceGeometry: PlaneGeometry) {
    this.drawFrontPlane(faceGeometry, otherGroup, hoverableGroup);
    this.drawBackPlane(faceGeometry, otherGroup, hoverableGroup);
    this.drawRightPlane(faceGeometry, otherGroup, hoverableGroup);
    this.drawLeftPlane(faceGeometry, otherGroup, hoverableGroup);
    this.drawTopPlane(faceGeometry, otherGroup, hoverableGroup);
    this.drawBottomPlane(faceGeometry, otherGroup, hoverableGroup);
  }

  private drawBottomPlane(faceGeometry: PlaneGeometry, otherGroup: Group, hoverableGroup: Group) {
    const bottomTexture = new TextureLoader().load('/assets/textures/bottom.png');
    const bottomTextureMaterial = new MeshBasicMaterial({
      map: bottomTexture,
      transparent: true,
    });
    const bottomPlaneTexture = new Mesh(faceGeometry, bottomTextureMaterial);
    bottomPlaneTexture.rotation.set(Math.PI / 2, 0, 0);
    bottomPlaneTexture.position.set(0, -this.BOX_DIMENSION / 2 - this.BOX_DIMENSION * 0.01, 0);
    otherGroup.add(bottomPlaneTexture);
    const bottomPlane = new Mesh(faceGeometry, this.createHoverMaterial());
    bottomPlane.rotation.set(Math.PI / 2, 0, 0);
    bottomPlane.position.set(0, -this.BOX_DIMENSION / 2, 0);
    hoverableGroup.add(bottomPlane);
  }

  private drawTopPlane(faceGeometry: PlaneGeometry, otherGroup: Group, hoverableGroup: Group) {
    const topTexture = new TextureLoader().load('/assets/textures/top.png');
    const topTextureMaterial = new MeshBasicMaterial({
      map: topTexture,
      transparent: true,
      side: DoubleSide,
    });
    const topPlaneTexture = new Mesh(faceGeometry, topTextureMaterial);
    topPlaneTexture.scale.y = -1;
    topPlaneTexture.rotation.set(Math.PI / 2, 0, 0);
    topPlaneTexture.position.set(0, this.BOX_DIMENSION / 2 + this.BOX_DIMENSION * 0.01, 0);
    otherGroup.add(topPlaneTexture);
    const topPlane = new Mesh(faceGeometry, this.createHoverMaterial());
    topPlane.rotation.set(Math.PI / 2, 0, 0);
    topPlane.position.set(0, this.BOX_DIMENSION / 2, 0);
    hoverableGroup.add(topPlane);
  }

  private drawLeftPlane(faceGeometry: PlaneGeometry, otherGroup: Group, hoverableGroup: Group) {
    const leftTexture = new TextureLoader().load('/assets/textures/left.png');
    const leftTextureMaterial = new MeshBasicMaterial({
      map: leftTexture,
      transparent: true,
    });
    const leftPlaneTexture = new Mesh(faceGeometry, leftTextureMaterial);
    leftPlaneTexture.rotation.set(0, -Math.PI / 2, 0);
    leftPlaneTexture.position.set(-this.BOX_DIMENSION / 2 - this.BOX_DIMENSION * 0.01, 0, 0);
    otherGroup.add(leftPlaneTexture);
    const leftPlane = new Mesh(faceGeometry, this.createHoverMaterial());
    leftPlane.rotation.set(0, Math.PI / 2, 0);
    leftPlane.position.set(-this.BOX_DIMENSION / 2, 0, 0);
    hoverableGroup.add(leftPlane);
  }

  private drawRightPlane(faceGeometry: PlaneGeometry, otherGroup: Group, hoverableGroup: Group) {
    const rightTexture = new TextureLoader().load('/assets/textures/right.png');
    const rightTextureMaterial = new MeshBasicMaterial({
      map: rightTexture,
      transparent: true,
    });
    const rightPlaneTexture = new Mesh(faceGeometry, rightTextureMaterial);
    rightPlaneTexture.rotation.set(0, Math.PI / 2, 0);
    rightPlaneTexture.position.set(this.BOX_DIMENSION / 2 + this.BOX_DIMENSION * 0.01, 0, 0);
    otherGroup.add(rightPlaneTexture);
    const rightPlane = new Mesh(faceGeometry, this.createHoverMaterial());
    rightPlane.rotation.set(0, Math.PI / 2, 0);
    rightPlane.position.set(this.BOX_DIMENSION / 2, 0, 0);
    hoverableGroup.add(rightPlane);
  }

  private drawBackPlane(faceGeometry: PlaneGeometry, otherGroup: Group, hoverableGroup: Group) {
    const frontTexture = new TextureLoader().load('/assets/textures/front.png');
    const frontTextureMaterial = new MeshBasicMaterial({
      map: frontTexture,
      transparent: true,
      side: DoubleSide,
    });
    const frontPlaneTexture = new Mesh(faceGeometry, frontTextureMaterial);
    frontPlaneTexture.scale.x = -1;
    frontPlaneTexture.position.set(0, 0, -this.BOX_DIMENSION / 2 - this.BOX_DIMENSION * 0.01);
    otherGroup.add(frontPlaneTexture);
    const frontPlane = new Mesh(faceGeometry, this.createHoverMaterial());
    frontPlane.position.set(0, 0, -this.BOX_DIMENSION / 2);
    hoverableGroup.add(frontPlane);
  }

  private drawFrontPlane(faceGeometry: PlaneGeometry, otherGroup: Group, hoverableGroup: Group) {
    const backTexture = new TextureLoader().load('/assets/textures/back.png');
    const backTextureMaterial = new MeshBasicMaterial({
      map: backTexture,
      transparent: true,
    });
    const backPlaneTexture = new Mesh(faceGeometry, backTextureMaterial);
    backPlaneTexture.position.set(0, 0, this.BOX_DIMENSION / 2);
    otherGroup.add(backPlaneTexture);
    const backPlane = new Mesh(faceGeometry, this.createHoverMaterial());
    backPlane.position.set(0, 0, this.BOX_DIMENSION / 2);
    hoverableGroup.add(backPlane);
  }

  private drawBottomEdges(group: Group, edgeGeometry: BufferGeometry) {
    const edge = new Mesh(edgeGeometry, this.createHoverMaterial());
    edge.rotation.set(Math.PI / 2, 0, 0);
    edge.position.set(-this.BOX_DIMENSION / 2, -this.BOX_DIMENSION / 2, 0);
    group.add(edge);

    const edge1 = new Mesh(edgeGeometry, this.createHoverMaterial());
    edge1.rotation.set(Math.PI / 2, Math.PI / 2, 0);
    edge1.position.set(this.BOX_DIMENSION / 2, -this.BOX_DIMENSION / 2, 0);
    group.add(edge1);

    const edge2 = new Mesh(edgeGeometry, this.createHoverMaterial());
    edge2.rotation.set(Math.PI / 2, 0, -Math.PI / 2);
    edge2.position.set(0, -this.BOX_DIMENSION / 2, this.BOX_DIMENSION / 2);
    group.add(edge2);

    const edge3 = new Mesh(edgeGeometry, this.createHoverMaterial());
    edge3.rotation.set(Math.PI / 2, 0, Math.PI / 2);
    edge3.position.set(0, -this.BOX_DIMENSION / 2, -this.BOX_DIMENSION / 2);
    group.add(edge3);
  }

  private drawTopEdges(group: Group, edgeGeometry: BufferGeometry) {
    const edge = new Mesh(edgeGeometry, this.createHoverMaterial());
    edge.rotation.set(-Math.PI / 2, 0, 0);
    edge.position.set(-this.BOX_DIMENSION / 2, this.BOX_DIMENSION / 2, 0);
    group.add(edge);

    const edge1 = new Mesh(edgeGeometry, this.createHoverMaterial());
    edge1.rotation.set(-Math.PI / 2, Math.PI / 2, 0);
    edge1.position.set(this.BOX_DIMENSION / 2, this.BOX_DIMENSION / 2, 0);
    group.add(edge1);

    const edge2 = new Mesh(edgeGeometry, this.createHoverMaterial());
    edge2.rotation.set(-Math.PI / 2, 0, Math.PI / 2);
    edge2.position.set(0, this.BOX_DIMENSION / 2, this.BOX_DIMENSION / 2);
    group.add(edge2);

    const edge3 = new Mesh(edgeGeometry, this.createHoverMaterial());
    edge3.rotation.set(-Math.PI / 2, 0, -Math.PI / 2);
    edge3.position.set(0, this.BOX_DIMENSION / 2, -this.BOX_DIMENSION / 2);
    group.add(edge3);
  }

  private drawVerticalEdges(group: Group, edgeGeometry: BufferGeometry): void {
    const edge = new Mesh(edgeGeometry, this.createHoverMaterial());
    edge.position.set(-this.BOX_DIMENSION / 2, 0, this.BOX_DIMENSION / 2);
    group.add(edge);

    const edge1 = new Mesh(edgeGeometry, this.createHoverMaterial());
    edge1.rotation.set(0, Math.PI / 2, 0);
    edge1.position.set(this.BOX_DIMENSION / 2, 0, this.BOX_DIMENSION / 2);
    group.add(edge1);

    const edge2 = new Mesh(edgeGeometry, this.createHoverMaterial());
    edge2.position.set(-this.BOX_DIMENSION / 2, 0, -this.BOX_DIMENSION / 2);
    edge2.rotation.set(0, -Math.PI / 2, 0);
    group.add(edge2);

    const edge3 = new Mesh(edgeGeometry, this.createHoverMaterial());
    edge3.rotation.set(0, -Math.PI, 0);
    edge3.position.set(this.BOX_DIMENSION / 2, 0, -this.BOX_DIMENSION / 2);
    group.add(edge3);
  }

  private drawBottomCorners(group: Group, cornerGeometry: BufferGeometry): void {
    const frontLeftCorner = this.createCornerMesh(
      cornerGeometry,
      new Vector3(-this.BOX_DIMENSION / 2, -this.BOX_DIMENSION / 2, -this.BOX_DIMENSION / 2),
      new Euler(Math.PI / 2, 0, 0),
    );
    group.add(frontLeftCorner);

    const leftBackCorner = this.createCornerMesh(
      cornerGeometry,
      new Vector3(-this.BOX_DIMENSION / 2, -this.BOX_DIMENSION / 2, this.BOX_DIMENSION / 2),
      new Euler(Math.PI / 2, 0, -Math.PI / 2),
    );
    group.add(leftBackCorner);

    const backRightConer = this.createCornerMesh(
      cornerGeometry,
      new Vector3(this.BOX_DIMENSION / 2, -this.BOX_DIMENSION / 2, this.BOX_DIMENSION / 2),
      new Euler(0, Math.PI / 2, 0),
    );
    group.add(backRightConer);

    const rightFrontCorner = this.createCornerMesh(
      cornerGeometry,
      new Vector3(this.BOX_DIMENSION / 2, -this.BOX_DIMENSION / 2, -this.BOX_DIMENSION / 2),
      new Euler(Math.PI / 2, 0, Math.PI / 2),
    );
    group.add(rightFrontCorner);
  }

  private drawTopCorners(group: Group, cornerGeometry: BufferGeometry) {
    const frontLeftCorner = this.createCornerMesh(
      cornerGeometry,
      new Vector3(-this.BOX_DIMENSION / 2, this.BOX_DIMENSION / 2, -this.BOX_DIMENSION / 2),
      new Euler(-Math.PI / 2, 0, -Math.PI / 2),
    );
    group.add(frontLeftCorner);

    const leftBackCorner = this.createCornerMesh(
      cornerGeometry,
      new Vector3(-this.BOX_DIMENSION / 2, this.BOX_DIMENSION / 2, this.BOX_DIMENSION / 2),
      new Euler(-Math.PI / 2, 0, 0),
    );
    group.add(leftBackCorner);

    const backRightConer = this.createCornerMesh(
      cornerGeometry,
      new Vector3(this.BOX_DIMENSION / 2, this.BOX_DIMENSION / 2, this.BOX_DIMENSION / 2),
      new Euler(0, Math.PI / 2, -Math.PI / 2),
    );
    group.add(backRightConer);

    const rightFrontCorner = this.createCornerMesh(
      cornerGeometry,
      new Vector3(this.BOX_DIMENSION / 2, this.BOX_DIMENSION / 2, -this.BOX_DIMENSION / 2),
      new Euler(-Math.PI / 2, Math.PI / 2, -Math.PI / 2),
    );
    group.add(rightFrontCorner);
  }

  private createCornerMesh(cornerGeometry: BufferGeometry, position: Vector3, rotation: Euler): Mesh {
    const corner = new Mesh(cornerGeometry, this.createHoverMaterial());
    corner.rotation.copy(rotation);
    corner.position.copy(position);
    return corner;
  }

  private createCenterPlaneGemetry(): PlaneGeometry {
    return new PlaneGeometry(
      this.BOX_DIMENSION - this.SIDE_SIZE * 2,
      this.BOX_DIMENSION - this.SIDE_SIZE * 2,
      this.BOX_DIMENSION - this.SIDE_SIZE * 2,
    );
  }

  private createEdgeGeometry(): BufferGeometry {
    const geometry = new BufferGeometry();
    // Vertices for two quads forming an L-shaped edge piece
    const vertices = new Float32Array([
      // First quad (back face)
      0, this.BOX_DIMENSION - 2 * this.SIDE_SIZE, 0,
      0, this.BOX_DIMENSION - 2 * this.SIDE_SIZE, -this.SIDE_SIZE,
      0, 0, -this.SIDE_SIZE,
      0, 0, -this.SIDE_SIZE,
      0, 0, 0,
      0, this.BOX_DIMENSION - 2 * this.SIDE_SIZE, 0,
      // Second quad (side face)
      0, this.BOX_DIMENSION - 2 * this.SIDE_SIZE, 0,
      this.SIDE_SIZE, this.BOX_DIMENSION - 2 * this.SIDE_SIZE, 0,
      this.SIDE_SIZE, 0, 0,
      this.SIDE_SIZE, 0, 0,
      0, 0, 0,
      0, this.BOX_DIMENSION - 2 * this.SIDE_SIZE, 0,
    ]);
    geometry.setAttribute('position', new BufferAttribute(vertices, 3));
    geometry.computeVertexNormals();
    geometry.translate(0, -(this.BOX_DIMENSION - 2 * this.SIDE_SIZE) / 2, 0);
    return geometry;
  }

  private createCornerGeometry(): BufferGeometry {
    const geometry = new BufferGeometry();
    // Vertices for three quads forming a corner piece
    const vertices = new Float32Array([
      // First quad (back face)
      0, this.SIDE_SIZE, 0,
      0, this.SIDE_SIZE, -this.SIDE_SIZE,
      0, 0, -this.SIDE_SIZE,
      0, 0, -this.SIDE_SIZE,
      0, 0, 0,
      0, this.SIDE_SIZE, 0,
      // Second quad (side face)
      0, this.SIDE_SIZE, 0,
      this.SIDE_SIZE, this.SIDE_SIZE, 0,
      this.SIDE_SIZE, 0, 0,
      this.SIDE_SIZE, 0, 0,
      0, 0, 0,
      0, this.SIDE_SIZE, 0,
      // Third quad (bottom face)
      0, 0, 0,
      0, 0, -this.SIDE_SIZE,
      this.SIDE_SIZE, 0, -this.SIDE_SIZE,
      this.SIDE_SIZE, 0, -this.SIDE_SIZE,
      this.SIDE_SIZE, 0, 0,
      0, 0, 0,
    ]);
    geometry.setAttribute('position', new BufferAttribute(vertices, 3));
    geometry.computeVertexNormals();
    return geometry;
  }

  private createHoverMaterial(): MeshBasicMaterial {
    return new MeshBasicMaterial({ color: 0xf8f8f8, side: DoubleSide });
  }
}
